import { Injectable, Input } from '@angular/core';
import {
  HTTPWrapperContext,
  XHR_STRATEGIES,
  REQUEST_METHODS,
  RequestConfigImplementation,
} from '@ptl/xhr';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';

import { PlanServiceResponse } from '../../interfaces/plan-service-response.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { comparePlansByIds } from '../../configs/reducers/plan-details.reducer';
import { environment } from 'src/environments/environment';
import { PlanTypesCollection } from '../../interfaces/plan-types-collection.interface';
import { setRecommendedPlanIds, savePlans, plansNeedRefreshedFromBackend } from '../../configs/reducers/plans.reducer';
import { RecommendedPlans } from '../../interfaces/recommended-plans.interface';
import { IndividualMarketOnExchangeProductsRequestPayload, IndividualMarketProductsRequestPayload } from '../../services/interfaces/shared/plan-demographics.interface';
import { OFF_EXCHANGE_INDIVIDUAL_PRODUCT_GROUPS, PRODUCT_GROUPS, PRODUCT_GROUP_NAMES_INDIVIDUAL } from '../../enums/product-groups.enum';
import { IndividualMarketStateStructure, RatingInfo } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { FormConfig, FormNavigationConfig, DefaultFormConfig } from '@ptl/forms';
import { PlanUtilityService } from '../plan-utility.service';
import { BrandingConfigurationService } from '@ptl/brand';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { Person } from 'src/app/angular-elements/form-wrappers/form-components/individual-market-persons/interfaces/person.interface';
import { DateService } from 'src/app/shared/services/date.service';
import { ContentStorageService } from '@ptl/global-components';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { EligibilityRenderable } from 'src/app/angular-elements/on-exchange-enrollment/interfaces/eligibility.interface';

import { Applicant, APPLICANT_RELATION } from 'src/app/angular-elements/plan-selection/services/interfaces/shared/plan-demographics/applicant.interface';


@Injectable({
  providedIn: 'root'
})
export class IndividualPlansService {
  forceValues = false;

  @Input() formConfig: DefaultFormConfig;

  planServiceUrl = '/retrieveIndividualPlans/individualplans';

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private brandService: BrandingConfigurationService,
    private date: DateService,
    private contentStorageService: ContentStorageService,
  ) { }


  plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();
  productGroup = PRODUCT_GROUPS.MEDICARE_ADVANTAGE;
  productType;

  /** VIEW-PLANS payload */
  createPayload(
    productGroup: PRODUCT_GROUP_NAMES_INDIVIDUAL
  ): IndividualMarketProductsRequestPayload | IndividualMarketOnExchangeProductsRequestPayload {

    const individualMarketState: IndividualMarketStateStructure = this.reducerUtilityService.getPayloadState('individualMarket');
    const onExchangeEligibilityState: OnExchangeEligibilityStateStructure = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const ddValue = this.brandService.getCurrentBrand().ddToken;
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    return this.createPayloadWithValues(productGroup, zipCountyState, ddValue, individualMarketState, onExchangeEligibilityState);
  }

  getServiceZoneValue(countyInfo: County, effectiveDateYear: string) {
    const data = [];

    /** HM ASP will use serviceZonesByPlanYear */
    if (
      (
        countyInfo.state === 'PA' ||
        countyInfo.state === 'WV' ||
        countyInfo.state === 'DE' ||
        countyInfo.state === 'NY'
      ) && effectiveDateYear
    ) {
      /**
       * Pennsylvania uses the SERVICE ZONES BY PLAN YEAR breakdown
       * All other regions use the return SERVICE ZONES breakdown
       */
      if (countyInfo?.serviceZonesByPlanYear?.[effectiveDateYear]) {
        countyInfo.serviceZonesByPlanYear[effectiveDateYear].forEach(zone => data.push(zone.value));
      }
    } else {
      countyInfo?.serviceZones?.forEach(zone => data.push(zone.value));
    }
    return data;
  }

  createPayloadWithValues(
    productGroup: PRODUCT_GROUP_NAMES_INDIVIDUAL,
    zipCountyState: ZipCountyStateStructure,
    ddValue: string,
    individualMarketState: IndividualMarketStateStructure,
    onExchangeEligibilityState: OnExchangeEligibilityStateStructure
  ): IndividualMarketProductsRequestPayload | IndividualMarketOnExchangeProductsRequestPayload {
    
    if (zipCountyState) {

      /** ASP ID */
      const aspId = environment.initData.brand.ASPID;

      /** Region && Rating Area */
      const region = zipCountyState.county && zipCountyState.county.region;
      const ratingArea = zipCountyState.county && zipCountyState.county.ratingArea;

      /** Default Time */
      const now = this.date.getTodayAsMoment();
      const nowFormatted = now.format('YYYY-MM-DD');
      const nowYearAsString = now.year().toString();

      // using contentStorage to avoid lots of interfacing for this config from the AEM component.
      const configFromStorage = this.contentStorageService.getContentMap('onExchangeRates');
      const onExchangeRatesString: string = configFromStorage.get('onExchangeRates');
      const onExchangeRates: boolean = (productGroup === 'On-Exchange' || onExchangeRatesString === 'true') ? true : false;

      /** Base Payload */
      const payload: IndividualMarketProductsRequestPayload = {
        applicants: [],
        aspId,
        attributes: {
          product_group: productGroup,
          onExchangeRates: onExchangeRates
        },
        ddToken: ddValue,
        effectiveDate: nowFormatted,
        market: 'Individual',
        ratingArea,
        region,
        serviceZone: this.getServiceZoneValue(zipCountyState.county, nowYearAsString),
        year: nowYearAsString
      };

      if (region === 'MINCR') {
        // TODO: convert this to a CONFIG somewhere.
        payload.attributes.DEFINING_REGION = zipCountyState.county.county;
      }

      if (OFF_EXCHANGE_INDIVIDUAL_PRODUCT_GROUPS.includes(productGroup.toUpperCase())) {
        /** OFF-EXCHANGE */
        const ratingInfo: RatingInfo = individualMarketState && individualMarketState.ratingInfo;

        if (ratingInfo) {

          /** Effective Date Info */
          const effectiveDateRaw = this.date.getDateAsMoment(ratingInfo.effectiveDate);
          const effectiveDateFormatted = effectiveDateRaw.format('YYYY-MM-DD');
          const effectiveDateYearString = effectiveDateRaw.year().toString();

          /** Applicant(s) Info */
          const applicantData = this._getApplicantPayloadInfo(ratingInfo, individualMarketState.childOnlyPolicy);

          payload.applicants = applicantData;
          payload.effectiveDate = effectiveDateFormatted;
          payload.serviceZone = this.getServiceZoneValue(zipCountyState.county, effectiveDateYearString);
          payload.year = effectiveDateYearString;

        }
      } else if (productGroup === 'On-Exchange') {
        /** ON-EXCHANGE */

        const activeProspectId = onExchangeEligibilityState.currentProspectId;
        const currentProspectData = onExchangeEligibilityState.prospects.filter(p => p.prospectId === activeProspectId);

        if (currentProspectData.length > 0) {
          const applicantData = this._getApplicantPayloadInfoONX(currentProspectData[0].data);
          const effectiveDateArray = currentProspectData[0].data.effectiveDate.split('/');
          const effectiveDate = effectiveDateArray[2] + '-' + effectiveDateArray[0] + '-' + effectiveDateArray[1]; //yyyy-mm-dd
          const year = effectiveDateArray[2];

          let eligibilityLevel = '1'; // must be single digit format for the service

          // use the CSR value at the root of the data model
          if (currentProspectData[0].data.CSR) {
            eligibilityLevel = String(Number(currentProspectData[0].data.CSRLevel));
          }

          payload.applicants = applicantData;
          payload.effectiveDate = effectiveDate;
          payload.year = year;

          (payload as IndividualMarketOnExchangeProductsRequestPayload).eligibilityLevel = eligibilityLevel;
          // likely have to make a new county list call to get zip code for user post-login?
        }
      }

      return payload;
    }

    return null;
  }

  _getApplicantPayloadInfoONX(onExchangeData: EligibilityRenderable): Applicant[] {
    const appArray = [];

    // first, need to find primary person's index, everything has to be based off the index
    // this is being done in two loops to make maintenance easier. originally was in one loop, but that started to have
    // issues when we encountered different FFM data
    let primaryIndex = 0;
    for (let i = 0; i < onExchangeData.applicants.length; i++) {
      if (onExchangeData.applicants[i].isEligible && onExchangeData.applicants[i].isPrimary) {
        primaryIndex = i;
      }
    }

    // iterate over applicants to format the appArray
    for (let i = 0; i < onExchangeData.applicants.length; i++) {

      if (onExchangeData.applicants[i].isEligible) {

        // collect dob for each applicant
        const dobSplit = onExchangeData.applicants[i].dateOfBirth.split('/'); // mm/dd/yyyy
        const formattedDob = dobSplit[2] + '-' + dobSplit[0] + '-' + dobSplit[1];

        let relationshipValue = '';
        if (onExchangeData.applicants[i].isPrimary) {

          // this index is the primary applicant, set eligibility level and relationship value
          console.log('primary applicants csr level', onExchangeData.applicants[i].CSRLevel);
          relationshipValue = APPLICANT_RELATION.SUBSCRIBER;

        } else {

          //non primary applicants must use the primary applicant's index to fetch the familyCodes array
          onExchangeData.applicants[primaryIndex].familyCodes.forEach(familyCode => {
            if (onExchangeData.applicants[i].personID === familyCode.person) {
              if (familyCode.codeValue === '01') {
                relationshipValue = APPLICANT_RELATION.SPOUSAL_PARTNER;
              } else {
                relationshipValue = APPLICANT_RELATION.DEPENDENT;
              }
            }
          });
        }

        appArray.push({
          // name: onExchangeData.applicants[i].fullName.firstName,
          dob: formattedDob,
          tobaccoStatus: onExchangeData.applicants[i].tobaccoInd,
          relationship: relationshipValue
        });

      }
    }

    return appArray;

  }

  _getApplicantPayloadInfo(ratingInfo: RatingInfo, childOnly: 'Yes' | 'No'): Applicant[] {
    const formatPerson = (
      type: APPLICANT_RELATION,
      person: Person
    ): Applicant => {
      const formatters = {
        SUBSCRIBER: (p: Person): Applicant => {
          return {
            dob: this.date.getDateAsFormattedString(p.dob, 'YYYY-MM-DD'),
            relationship: APPLICANT_RELATION.SUBSCRIBER,
            tobaccoStatus: p.tobaccoIndicator ? 'Y' : 'N',
            personId: p.personId
          };
        },
        DEPENDENT: (p: Person): Applicant => {
          return {
            dob: this.date.getDateAsFormattedString(p.dob, 'YYYY-MM-DD'),
            relationship: APPLICANT_RELATION.DEPENDENT,
            tobaccoStatus: p.tobaccoIndicator ? 'Y' : 'N',
            personId: p.personId
          };
        },
        SPOUSE: (p: Person): Applicant => {
          return {
            dob: this.date.getDateAsFormattedString(p.dob, 'YYYY-MM-DD'),
            relationship: APPLICANT_RELATION.SPOUSAL_PARTNER,
            tobaccoStatus: p.tobaccoIndicator ? 'Y' : 'N',
            personId: p.personId
          };
        }
      };

      /** Check if person object has any missing attributes - maybe remove after cleaning form */
      const anyFormValsMissing = (() => {
        let missingVal = false;
        Object.keys(person).forEach(key => { if (!person[key] && person[key] !== false && ['dob', 'tobaccoStatus', 'relationship'].includes(key)) { missingVal = true; } });
        return missingVal;
      })();

      /** Don't return a bad person object - maybe remove after cleaning form */
      if (!anyFormValsMissing) {
        return formatters[type](person);
      } else {
        return null;
      }
    };

    const data: Applicant[] = [];
    const isChildOnly = childOnly && childOnly.toUpperCase() === 'YES';
    if (isChildOnly) {

      /** Dependent data only */
      if (ratingInfo.dependents) {
        Object.keys(ratingInfo.dependents).forEach((key: number | string, index: number) => {
          const formattedApplicant: Applicant = formatPerson(APPLICANT_RELATION.DEPENDENT, ratingInfo.dependents[key]);
          if (formattedApplicant) {
            data.push(formattedApplicant);
          }
        });
      }

    } else {

      /** Subscriber data only */
      if (ratingInfo.subscriber) {
        Object.keys(ratingInfo.subscriber).forEach((key: number | string, index: number) => {
          const formattedApplicant: Applicant = formatPerson(APPLICANT_RELATION.SUBSCRIBER, ratingInfo.subscriber[key]);
          if (formattedApplicant) {
            data.push(formattedApplicant);
          }
        });
      }

      /** Spousal-Partner data only */
      if (ratingInfo['spousal-partner']) {
        Object.keys(ratingInfo['spousal-partner']).forEach((key: number | string, index: number) => {
          const formattedApplicant: Applicant = formatPerson(APPLICANT_RELATION.SPOUSAL_PARTNER, ratingInfo['spousal-partner'][key]);
          if (formattedApplicant) {
            data.push(formattedApplicant);
          }
        });
      }

      /** Dependent data only */
      if (ratingInfo.dependents) {
        Object.keys(ratingInfo.dependents).forEach((key: number | string, index: number) => {
          const formattedApplicant: Applicant = formatPerson(APPLICANT_RELATION.DEPENDENT, ratingInfo.dependents[key]);
          if (formattedApplicant) {
            data.push(formattedApplicant);
          }
        });
      }

    }

    return data;
  }

  async getPlans(payload: IndividualMarketProductsRequestPayload): Promise<PlanServiceResponse> {

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: this.planServiceUrl,
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.GOOGLE_CLOUD);

    return this.httpClient.makeRequest<PlanServiceResponse>(requestConfig).catch(err => {
      console.error(err);
      return err;
    });
  }

  storePlans(plansResponse: PlanTypesCollection): void {
    this.ngRedux.dispatch(comparePlansByIds([]));
    this.ngRedux.dispatch(savePlans(plansResponse));
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(false));
  }

  storeRecommendedPlans(recommendedPlans: RecommendedPlans): void {
    this.ngRedux.dispatch(setRecommendedPlanIds(recommendedPlans));
  }

  postRetrievalHooks(combinedPlansResponse: PlanTypesCollection, combinedRecommendedPlans: RecommendedPlans[]) { }


}
